import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  inject,
  Input,
  ViewChild,
  TemplateRef,
  AfterViewInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
  Subject,
  of,
  switchMap,
  debounceTime,
  distinctUntilChanged,
  tap,
  catchError,
  EMPTY,
} from 'rxjs';

import { CanComponentDeactivate } from '../../../Guard/pending-changes.guard';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';
import { PaymentModalComponent } from '../billing/payment-modal/payment-modal.component';

// Custom Services (Assume these are available for compilation)
import { ServiceBillingService, ServiceItem } from './service-billing.service';
import { RouterModule } from '@angular/router';

type UUID = string;

// ServiceCartItem Interface
interface ServiceCartItem {
  id: string;
  serviceId: string;
  name: string;
  price: number;
  totalAmount: number; // Net amount (same as price for now, before overall discount)
  gstRate: number; // GST Percentage
  gstAmount: number; // Calculated GST Amount
  grandTotal: number; // Total including GST
}

// ServiceTotals Interface
interface ServiceTotals {
  subTotal: number;
  discountTotal: number; // Overall discount
  finalTotal: number;
  gstAmount: number; // Sum of all line-item GST amounts
  grandTotal: number; // finalTotal + gstAmount - discountTotal
}

interface Recipient {
  id: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  clientId: string;
}

interface InvoiceItemPayload {
  serviceId: string;
  total: number;
  providerId: string;
}

// NEW INTERFACE for Payment Details (matches the expected payload and modal output)
interface PaymentDetail {
  mode: 'CASH' | 'CARD' | 'UPI';
  amount: number;
  cashTendered?: number; // Only for CASH transactions if applicable
}

// UPDATED ServiceInvoicePayload to include payments
interface ServiceInvoicePayload {
  recipientId: string;
  invoiceDate: string;
  total: number; // Grand Total (finalTotal + gstAmount - discount)
  gstAmount: number; // Total calculated GST amount
  discount: number; // Overall discount
  items: InvoiceItemPayload[];
  payments: PaymentDetail[];
  notes?: string;
}

@Component({
  selector: 'app-service-billing',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './service-billing.component.html',
})
export class ServiceBillingComponent
  implements OnInit, AfterViewInit, CanComponentDeactivate
{
  private serviceBillingService = inject(ServiceBillingService);
  private confirmDialog = inject(ConfirmDialogService);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);

  private isSaved = false;

  @Input() selectedCustomer: Recipient | null = null;

  @ViewChild('serviceOwnerModal') serviceOwnerModalRef!: TemplateRef<any>;

  providers: { id: string; name: string; email: string }[] = [];
  providerSearchTerm = '';
  providersLoading = false;
  selectedProvider: { id: string; name: string; email: string } | null = null;
  newProvider: { name?: string; email?: string } = {};
  selectedProviderId: string | null = null;
  providerInput$ = new Subject<string>();

  allServices: ServiceItem[] = [];
  serviceSearchTerm = '';
  productsLoading = false;

  currentPage = 1;
  itemsPerPage = 10;
  totalServiceItems = 0;

  serviceCart: ServiceCartItem[] = [];
  manualServiceName: string = '';

  serviceGrandTotal: ServiceTotals = this.getInitialTotals();
  overallDiscount: number = 0;
  discountPercentage: number = 0; // Calculated or Displayed Percentage
  discountMode: 'PERCENTAGE' | 'AMOUNT' = 'PERCENTAGE';
  manualDiscountValue: number = 0; // User Input Value

  drafts: any[] = [];
  activeDraftIndex: number | null = null;
  isDraftOpen = false;

  serviceInput$ = new Subject<string>();
  recipientInput$ = new Subject<string>();
  recipients: Recipient[] = [];
  recipientSearchTerm: string = '';
  recipientsLoading = false;
  selectedRecipientId: string | null = null;
  billingNotes: string = '';
  newCustomer: {
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
    clientId?: string;
  } = {};

  ngOnInit(): void {
    this.loadDrafts();
    this.setupServiceSearch();
    this.fetchServices(
      this.currentPage,
      this.itemsPerPage,
      this.serviceSearchTerm,
    );
    this.fetchRecipients(1, 10);
    this.setupRecipientSearch();
    this.setupProviderSearch();
  }

  ngAfterViewInit() {
    // Note: If using bootstrap 5.2+, you might need to use `new bootstrap.Offcanvas(element)`
    // to correctly manage offcanvas events in Angular without relying on jQuery/global bootstrap.
    // For this context, we'll keep the direct DOM manipulation for demonstration.
    const draftCurtain = document.getElementById('draftCurtain');
    if (draftCurtain) {
      // Use the standard way to check if events are supported or added
      // These listeners might need the full Bootstrap JS library to correctly fire `shown.bs.offcanvas`/`hidden.bs.offcanvas`.
      draftCurtain.addEventListener(
        'shown.bs.offcanvas',
        () => (this.isDraftOpen = true),
      );
      draftCurtain.addEventListener(
        'hidden.bs.offcanvas',
        () => (this.isDraftOpen = false),
      );
    }
  }

  fetchRecipients(
    page: number = 1,
    limit: number = 10,
    search: string = '',
  ): void {
    this.recipientsLoading = true;
    this.recipients = [];

    this.serviceBillingService
      .getRecipients({ page, limit, search })
      .pipe(
        tap(() => (this.recipientsLoading = false)),
        catchError((err) => {
          console.error('Failed to load recipients:', err);
          this.toastr.error('Failed to load recipients.', 'API Error');
          this.recipientsLoading = false;
          return EMPTY;
        }),
      )
      .subscribe((response: any) => {
        this.recipients = response.data ?? response.items ?? [];
      });
  }

  setupRecipientSearch(): void {
    this.recipientInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((term) => {
          this.recipientsLoading = true;
          this.recipients = [];
          this.recipientSearchTerm = term;
        }),
        switchMap(() =>
          this.serviceBillingService
            .getRecipients({
              page: 1,
              limit: 10,
              search: this.recipientSearchTerm,
            })
            .pipe(
              catchError((err) => {
                console.error('Recipient search error:', err);
                this.toastr.error('Search failed. Please try again.', 'Error');
                return of({
                  items: [],
                  meta: { totalItems: 0, currentPage: 1 },
                } as any);
              }),
            ),
        ),
      )
      .subscribe((response) => {
        this.recipientsLoading = false;
        this.recipients = response.items ?? response.data ?? [];
      });
  }

  onRecipientSearch(): void {
    this.recipientInput$.next(this.recipientSearchTerm.trim());
  }

  selectRecipient(recipient: Recipient, closeBtn: HTMLElement): void {
    this.selectedCustomer = recipient;
    this.selectedRecipientId = recipient.id;
    this.modalService.dismissAll();
    if (closeBtn) {
      closeBtn.click();
    }
    this.toastr.success(
      `${recipient.name} selected as customer.`,
      'Customer Selected',
    );
  }

  fetchProviders(search: string = ''): void {
    this.providersLoading = true;
    this.providers = [];

    this.serviceBillingService
      .getProviders({ page: 1, limit: 10, search })
      .pipe(
        tap(() => (this.providersLoading = false)),
        catchError((err) => {
          console.error('Failed to load providers:', err);
          this.toastr.error('Failed to load service owners.', 'API Error');
          this.providersLoading = false;
          return EMPTY;
        }),
      )
      .subscribe((response: any) => {
        this.providers = response.data ?? response.items ?? response ?? [];
        console.log('Fetched Service Providers:', this.providers);
      });
  }

  setupProviderSearch(): void {
    this.providerInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((term) => {
          this.providersLoading = true;
          this.providers = [];
          this.providerSearchTerm = term;
        }),
        switchMap(() =>
          this.serviceBillingService
            .getProviders({
              page: 1,
              limit: 10,
              search: this.providerSearchTerm,
            })
            .pipe(
              catchError((err) => {
                console.error('Provider search error:', err);
                this.toastr.error('Search failed. Please try again.', 'Error');
                return of({
                  items: [],
                  meta: { totalItems: 0, currentPage: 1 },
                } as any);
              }),
            ),
        ),
      )
      .subscribe((response) => {
        this.providersLoading = false;
        this.providers = response.items ?? response.data ?? [];
        console.log('Fetched Service Providers setup:', this.providers);
      });
  }

  onProviderSearch(): void {
    console.log('Provider Search Term:', this.providerSearchTerm);
    this.providerInput$.next(this.providerSearchTerm.trim());
  }

  selectProvider(provider: { id: string; name: string; email: string }) {
    this.selectedProvider = provider;
    this.selectedProviderId = provider.id;
    this.toastr.success(
      `${provider.name} selected as Service Owner.`,
      'Service Owner Selected',
    );
    this.modalService.dismissAll();
  }

  addNewProvider(payload: { name?: string; email?: string }) {
    const name = payload.name?.trim();
    if (!name) {
      this.toastr.warning('Name is required to create a service owner.');
      return;
    }

    const requestPayload: { name: string; email?: string } = {
      name,
      email: payload.email?.trim(),
    };

    this.serviceBillingService.addProvider(requestPayload).subscribe({
      next: (res: any) => {
        this.toastr.success('Service owner added successfully!');
        this.fetchProviders();
      },
      error: (err: any) => {
        this.toastr.error('Failed to add service owner.');
        console.error('Add Provider API Error:', err);
      },
    });
  }

  addNewRecipient(payload: any): void {
    this.serviceBillingService.addRecipient(payload).subscribe({
      next: (res) => {
        this.toastr.success('Recipient added successfully!');
        this.fetchRecipients();
      },
      error: (err) => {
        this.toastr.error('Failed to add recipient.');
        console.error('Add Recipient API Error:', err);
      },
    });
  }

  getInitialTotals(): ServiceTotals {
    return {
      subTotal: 0,
      discountTotal: 0,
      finalTotal: 0,
      gstAmount: 0,
      grandTotal: 0,
    };
  }

  trackService(index: number, item: ServiceItem | ServiceCartItem): string {
    return item.id || index.toString();
  }

  isAdminOrStoreManager(): boolean {
    return true;
  }

  fetchServices(page: number, limit: number, searchTerm: string = ''): void {
    this.productsLoading = true;
    this.allServices = [];

    this.serviceBillingService
      .getServices(page, limit)
      .pipe(
        tap(() => (this.productsLoading = false)),
        catchError((err) => {
          console.error('Failed to load services:', err);
          this.toastr.error('Failed to load available services.', 'API Error');
          this.productsLoading = false;
          return EMPTY;
        }),
      )
      .subscribe((response: any) => {
        const serviceItems = response.data ?? response.items ?? [];
        const meta = response.meta ?? {
          totalItems: serviceItems.length,
          currentPage: 1,
        };

        if (this.serviceSearchTerm) {
          this.allServices = serviceItems.filter((s: ServiceItem) =>
            s.name.toLowerCase().includes(this.serviceSearchTerm.toLowerCase()),
          );
        } else {
          this.allServices = serviceItems;
        }

        this.totalServiceItems = meta.totalItems;
        this.currentPage = meta.currentPage;
      });
  }

  setupServiceSearch(): void {
    this.serviceInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((term) => {
          this.productsLoading = true;
          this.allServices = [];
          this.serviceSearchTerm = term;
        }),
        switchMap(() =>
          this.serviceBillingService
            .getServices(1, this.itemsPerPage, this.serviceSearchTerm)
            .pipe(
              catchError((err) => {
                console.error('Service search error:', err);
                this.toastr.error('Search failed. Please try again.', 'Error');
                return of({
                  items: [],
                  meta: { totalItems: 0, currentPage: 1 },
                } as any);
              }),
            ),
        ),
      )
      .subscribe((response) => {
        this.productsLoading = false;
        this.allServices = response.items ?? response.data ?? [];

        this.totalServiceItems =
          response.meta?.totalItems ?? this.allServices.length;
        this.currentPage = 1;
      });
  }

  onServiceSearch(): void {
    this.serviceInput$.next(this.serviceSearchTerm.trim());
  }

  addServiceToCart(service: ServiceItem): void {
    const alreadyInCart = this.serviceCart.some(
      (item) => item.serviceId === service.id,
    );
    if (alreadyInCart) {
      this.toastr.warning(
        `${service.name} is already in the cart.`,
        'Duplicate Service',
      );
      return;
    }

    const gstRate = (service as any).gstRate || 0;

    const newItem: ServiceCartItem = {
      id: `${service.id}-${Date.now()}`,
      serviceId: service.id,
      name: service.name,
      price: service.price,
      totalAmount: 0,
      gstRate: gstRate,
      gstAmount: 0,
      grandTotal: 0,
    };

    this.serviceCart.push(newItem);
    this.calculateServiceTotals();
  }

  onManualServiceEnter(): void {
    const name = this.manualServiceName.trim();
    if (!name) return;

    const duplicateManual = this.serviceCart.some(
      (item) =>
        item.serviceId === 'manual' &&
        item.name.toLowerCase() === name.toLowerCase(),
    );

    if (duplicateManual) {
      this.toastr.warning(
        `"${name}" is already added as a manual service.`,
        'Duplicate Manual Service',
      );
      return;
    }

    const manualService: ServiceCartItem = {
      id: 'manual-' + Date.now(),
      serviceId: 'manual',
      name,
      price: 0,
      totalAmount: 0,
      gstRate: 0,
      gstAmount: 0,
      grandTotal: 0,
    };

    this.serviceCart.push(manualService);
    this.manualServiceName = '';
    this.calculateServiceTotals();
  }

  removeService(index: number): void {
    this.serviceCart.splice(index, 1);
    this.calculateServiceTotals();
  }

  clearServiceCart(): void {
    this.serviceCart = [];
    this.serviceGrandTotal = this.getInitialTotals();

    // Reset Customer
    this.selectedCustomer = null;
    this.selectedRecipientId = null;

    // Reset Provider
    this.selectedProvider = null;
    this.selectedProviderId = null;

    // Reset Discounts
    this.manualDiscountValue = 0;
    this.discountPercentage = 0;
    this.overallDiscount = 0;
    this.billingNotes = '';
    // this.discountMode = 'PERCENTAGE'; // Optional: keep last mode or reset
  }

  calculateServiceTotals(): void {
    if (!this.serviceCart.length) {
      this.serviceGrandTotal = this.getInitialTotals();
      this.overallDiscount = 0;
      return;
    }

    let subTotal = 0;
    let totalGSTAmount = 0;

    this.serviceCart.forEach((item) => {
      const price = Number(item.price) || 0;
      const gstRate = Number(item.gstRate) || 0;

      const gstAmount = +(price * (gstRate / 100)).toFixed(2);
      const itemTotal = +(price + gstAmount).toFixed(2);

      item.totalAmount = price;
      item.gstAmount = gstAmount;
      item.grandTotal = itemTotal;

      subTotal += price;
      totalGSTAmount += gstAmount;
    });

    const totalBeforeDiscount = subTotal + totalGSTAmount;

    let discountAmount = 0;

    if (this.discountMode === 'PERCENTAGE') {
      const userValue = Number(this.manualDiscountValue) || 0;
      // In percentage mode, the manual value IS the percentage
      this.discountPercentage = userValue;
      discountAmount = totalBeforeDiscount * (userValue / 100);
    } else {
      // AMOUNT mode
      const userValue = Number(this.manualDiscountValue) || 0;
      discountAmount = userValue;
      // Calculate what this amount is in percentage for display/reference
      this.discountPercentage =
        totalBeforeDiscount > 0
          ? (discountAmount / totalBeforeDiscount) * 100
          : 0;
    }

    // Restriction: Discount cannot exceed the total amount
    if (discountAmount > totalBeforeDiscount) {
      this.toastr.warning(
        'Discount cannot be more than the total amount.',
        'Invalid Discount',
      );
      discountAmount = totalBeforeDiscount;

      // Sync back the restricted value to inputs if needed,
      // but usually better to just cap the calc and let user correct it.
      // For Amount mode, we might want to cap the percentage or vice-versa.
    }

    this.overallDiscount = discountAmount;
    // Ensure grand total doesn't go below 0 (redundant now but safe)
    let finalGrandTotal = totalBeforeDiscount - discountAmount;
    if (finalGrandTotal < 0) {
      finalGrandTotal = 0;
    }
    finalGrandTotal = +finalGrandTotal.toFixed(2);

    this.serviceGrandTotal = {
      subTotal: +subTotal.toFixed(2),
      discountTotal: +discountAmount.toFixed(2),
      finalTotal: +totalBeforeDiscount.toFixed(2), // Total before discount
      gstAmount: +totalGSTAmount.toFixed(2),
      grandTotal: finalGrandTotal,
    };

    this.serviceCart = [...this.serviceCart];
  }

  saveServiceDraft(): void {
    const draft = {
      serviceCart: this.serviceCart,
      total: this.serviceGrandTotal || this.getInitialTotals(),
      timestamp: new Date(),
    };
    const existing = JSON.parse(localStorage.getItem('serviceDrafts') || '[]');
    if (this.activeDraftIndex !== null) existing[this.activeDraftIndex] = draft;
    else existing.push(draft);
    localStorage.setItem('serviceDrafts', JSON.stringify(existing));
    this.loadDrafts();
    this.clearServiceCart();
    this.activeDraftIndex = null;
    this.isSaved = true;
    this.toastr.success('Service draft saved.');
  }

  loadDrafts(): void {
    // Safely parse drafts and handle potential missing 'total' in old drafts
    const rawDrafts = JSON.parse(localStorage.getItem('serviceDrafts') || '[]');
    this.drafts = rawDrafts.map((draft: any) => ({
      ...draft,
      // Ensure 'total' exists for safe rendering in the template
      total: draft.total || this.getInitialTotals(),
    }));
  }

  resumeDraft(index: number): void {
    const draft = this.drafts[index];
    this.serviceCart = draft?.serviceCart ?? [];
    this.activeDraftIndex = index;
    this.calculateServiceTotals();
  }

  placeServiceOrder(): void {
    if (!this.selectedCustomer || !this.selectedCustomer.id) {
      this.toastr.error(
        'Please select or add a customer before placing the order.',
      );
      return;
    }

    // 1. Pre-check Service Owner ID (Provider ID)
    const providerId = this.selectedProviderId;
    if (!providerId) {
      this.toastr.error(
        'Please select a Service Owner before placing the order.',
      );
      return;
    }

    // 2. Map items, including the providerId in each item
    const payloadItems: InvoiceItemPayload[] = this.serviceCart
      // Ensure only valid, priced services are included
      .filter((item) => item.serviceId !== 'manual' && item.price > 0)
      .map((item) => {
        return {
          serviceId: item.serviceId,
          total: item.totalAmount,
          providerId: providerId,
        };
      });

    if (payloadItems.length === 0) {
      this.toastr.error(
        'Bill total is zero or contains only manual/unpriced services.',
      );
      return;
    }

    const finalGrandTotal = this.serviceGrandTotal.grandTotal;

    // Open Payment Modal
    const modalRef = this.modalService.open(PaymentModalComponent);
    modalRef.componentInstance.totalAmount = finalGrandTotal;

    // Use .then() to wait for the modal to close and retrieve the payments array
    modalRef.result
      .then((payments: PaymentDetail[]) => {
        // Payments are now correctly typed and received

        // Ensure payments array is valid and non-empty (optional, but safer)
        if (!payments || payments.length === 0) {
          this.toastr.error('Payment details were not provided.', 'Error');
          return;
        }

        const totalGSTAmount = this.serviceGrandTotal.gstAmount;

        // 3. Build the final payload, now including payments
        const payload: ServiceInvoicePayload = {
          recipientId: this.selectedCustomer!.id, // Use ! since we checked existence above
          invoiceDate: new Date().toISOString().split('T')[0],
          total: finalGrandTotal,
          gstAmount: totalGSTAmount,
          discount: this.serviceGrandTotal.discountTotal,
          items: payloadItems,
          payments: payments,
          notes: this.billingNotes.trim() || undefined,
        };

        this.serviceBillingService.createServiceInvoice(payload).subscribe({
          next: (invoiceResponse: any) => {
            const invoiceId = invoiceResponse.id || invoiceResponse.invoiceId;

            this.toastr.success('Service Invoice Placed Successfully!');

            if (invoiceId) {
              this.generateInvoicePDF(invoiceId);
              this.clearServiceCart();
              // Provider reset is now handled in clearServiceCart
              this.isSaved = true;
            } else {
              this.toastr.warning(
                'Invoice created but ID not returned for PDF generation.',
                'Warning',
              );
              this.clearServiceCart();
              this.isSaved = true;
            }
          },
          error: (err) => {
            this.toastr.error('Failed to place service invoice.', 'API Error');
            console.error('Service Invoice API Error:', err);
          },
        });
      })
      .catch(() => {
        // This runs if the user dismisses the modal (e.g., clicks outside, presses escape)
        this.toastr.info('Order placement cancelled by user.');
      });
  }

  generateInvoicePDF(id: string): void {
    const pdfPayload = {};

    this.serviceBillingService.generateInvoice(id, pdfPayload).subscribe({
      next: (blobResponse: any) => {
        if (blobResponse instanceof Blob) {
          this.autoDownloadBlob(blobResponse, `ServiceInvoice-${id}.pdf`);
          this.toastr.info('Invoice PDF download started.', 'Download');
        } else {
          this.toastr.warning(
            'PDF generation failed: Server did not return a file.',
            'Download Error',
          );
          console.error('PDF response was not a Blob:', blobResponse);
        }
      },
      error: (err) => {
        this.toastr.warning(
          'Invoice created, but PDF generation failed.',
          'PDF Error',
        );
        console.error('PDF Generation API Error:', err);
      },
    });
  }

  private autoDownloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  async canDeactivate(): Promise<boolean> {
    if (this.isSaved) return true;

    if (this.serviceCart.length > 0) {
      return this.confirmDialog.confirm({
        title: 'Discard service bill?',
        message:
          'You have unsaved items in the **Service Bill**. Are you sure you want to leave this page?',
        confirmText: 'Leave',
        cancelText: 'Stay',
        size: 'lg',
      });
    }
    return true;
  }

  openServiceOwnerModal() {
    if (this.serviceOwnerModalRef) {
      // 1. Reset state for a fresh modal view
      this.providerSearchTerm = '';
      this.newProvider = {};

      // 2. Fetch the initial list of providers
      this.fetchProviders();

      // 3. Open the modal
      this.modalService.open(this.serviceOwnerModalRef, {
        size: 'lg',
        centered: true,
      });
    } else {
      console.error('Service Owner Modal TemplateRef not found.');
      this.toastr.error('Could not open the service owner modal.', 'Error');
    }
  }
  get reversedDrafts(): any[] {
    return [...this.drafts].reverse();
  }
}
